import React, { useEffect, useRef, useState } from 'react';
import Icon from 'components/AppIcon';
import DeleteConfirmModal from 'components/ui/DeleteConfirmModal';

const STATUS_CONFIG = {
  New: { bg: '#DBEAFE', color: '#1D4ED8', label: 'New' },
  Contacted: { bg: '#FEF9C3', color: '#A16207', label: 'Contacted' },
  Qualified: { bg: '#DCFCE7', color: '#15803D', label: 'Qualified' },
  Unqualified: { bg: '#FEE2E2', color: '#B91C1C', label: 'Unqualified' },
  Won: { bg: '#D1FAE5', color: '#065F46', label: 'Won' },
  Lost: { bg: '#F3F4F6', color: '#4B5563', label: 'Lost' },
};

function formatRupees(value) {
  if (value >= 10000000) return `₹${(value / 10000000)?.toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000)?.toFixed(1)}L`;
  return `₹${value?.toLocaleString('en-IN')}`;
}

function mapLeadStatus(status) {
  if (typeof status === 'string') {
    return status;
  }

  const statusMap = {
    1: 'New',
    2: 'Contacted',
    3: 'Qualified',
    4: 'Unqualified',
    5: 'Won',
    6: 'Lost',
  };

  return statusMap[status] || 'New';
}

function mapLeadFromApi(lead) {
  const notes = String(lead?.notes || '').trim();
  const noteParts = notes
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part && part !== '-');

  let profession = '-';
  let experience = '-';

  if (noteParts.length >= 2) {
    [profession, experience] = noteParts;
  } else if (noteParts.length === 1) {
    const onlyPart = noteParts[0];
    const isBootcampLead = (lead?.source || '').toLowerCase() === 'bootcamp';
    const looksLikeExperience = /(year|yr|month|exp|\d)/i.test(onlyPart);
    if (isBootcampLead || looksLikeExperience) {
      experience = onlyPart;
    } else {
      profession = onlyPart;
    }
  }

  return {
    id: lead?.id,
    name: lead?.fullName || '',
    company: lead?.company || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    source: lead?.source || 'Website',
    rep: lead?.assignedTo || 'Unassigned',
    value: Number(lead?.estimatedValue || 0),
    status: mapLeadStatus(lead?.status),
    lastActivity: lead?.updatedAtUtc ? new Date(lead.updatedAtUtc)?.toLocaleString() : '-',
    timestamp: lead?.updatedAtUtc ? new Date(lead.updatedAtUtc)?.toLocaleString() : '-',
    experience,
    profession,
  };
}

export default function LeadsTable({ searchQuery, filters, initialLeads = [], onEditLead, onDeleteLead, onStatusChange, canDelete = true, onAddNew }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [editingId, setEditingId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [topScrollbarWidth, setTopScrollbarWidth] = useState(1300);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const syncSourceRef = useRef(null);

  useEffect(() => {
    setLeads((initialLeads || []).map(mapLeadFromApi));
  }, [initialLeads]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filters]);

  useEffect(() => {
    const updateTopScrollbarWidth = () => {
      const measuredWidth = tableRef.current?.scrollWidth || 1300;
      setTopScrollbarWidth(measuredWidth);
    };

    updateTopScrollbarWidth();
    window.addEventListener('resize', updateTopScrollbarWidth);

    return () => {
      window.removeEventListener('resize', updateTopScrollbarWidth);
    };
  }, [leads, searchQuery, filters]);

  const syncScroll = (source, target) => {
    if (!source || !target) {
      return;
    }

    if (syncSourceRef.current === target) {
      return;
    }

    syncSourceRef.current = source;
    target.scrollLeft = source.scrollLeft;

    window.requestAnimationFrame(() => {
      if (syncSourceRef.current === source) {
        syncSourceRef.current = null;
      }
    });
  };

  const handleTopScroll = () => {
    syncScroll(topScrollRef.current, tableScrollRef.current);
  };

  const handleTableScroll = () => {
    syncScroll(tableScrollRef.current, topScrollRef.current);
  };

  const filtered = leads?.filter((lead) => {
    const q = searchQuery?.toLowerCase() || '';
    const matchesSearch = !q || [lead?.name, lead?.company, lead?.email, lead?.phone, lead?.rep, lead?.source, lead?.profession]?.some((f) => f?.toLowerCase()?.includes(q));
    const matchesStatus = !filters?.status || filters?.status === 'all' || lead?.status === filters?.status;
    const matchesRep = !filters?.rep || filters?.rep === 'all' || lead?.rep === filters?.rep;
    const matchesSource = !filters?.source || filters?.source === 'all' || lead?.source === filters?.source;
    return matchesSearch && matchesStatus && matchesRep && matchesSource;
  });

  const sorted = [...(filtered || [])]?.sort((a, b) => {
    let av = a?.[sortField], bv = b?.[sortField];
    if (typeof av === 'string') av = av?.toLowerCase();
    if (typeof bv === 'string') bv = bv?.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelectedIds(selectedIds?.length === sorted?.length ? [] : sorted?.map((l) => l?.id));
  };

  const handleStatusChange = async (id, newStatus) => {
    const previousLeads = leads;
    setLeads((prev) => prev?.map((l) => l?.id === id ? { ...l, status: newStatus } : l));
    setEditingId(null);

    if (!onStatusChange) {
      return;
    }

    try {
      await onStatusChange(id, newStatus);
    } catch {
      setLeads(previousLeads);
    }
  };

  const handleBulkExport = () => {
    const rows = leads?.filter((lead) => selectedIds?.includes(lead?.id));
    const headers = ['Name', 'Timestamp', 'Email', 'Phone', 'Profession', 'Experience', 'Company', 'Source', 'Assigned Rep', 'Est. Value', 'Status'];
    const csv = [
      headers.join(','),
      ...rows?.map((lead) => [lead?.name, lead?.timestamp, lead?.email, lead?.phone, lead?.profession, lead?.experience, lead?.company, lead?.source, lead?.rep, lead?.value, lead?.status]
        .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')) || [],
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSelectedIds([]);
  };

  const pageCount = Math.max(1, Math.ceil(sorted?.length / PAGE_SIZE));
  const paged = sorted?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const firstShown = sorted?.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, sorted?.length);

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={12}
      color={sortField === field ? 'var(--color-primary)' : 'var(--color-text-secondary)'}
    />
  );

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-card)' }}>
      {/* Bulk actions */}
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: 'var(--color-primary)', borderBottom: '1px solid var(--color-border)' }}>
          <span className="font-caption text-sm font-medium text-white">{selectedIds?.length} selected</span>
          <button onClick={handleBulkExport} className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white text-xs font-caption transition-colors">
            <Icon name="Download" size={12} color="white" /> Export
          </button>
          <button className="ml-auto text-white/70 hover:text-white" onClick={() => setSelectedIds([])}>
            <Icon name="X" size={14} color="currentColor" />
          </button>
        </div>
      )}

      {/* Table */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        className="overflow-x-auto overflow-y-hidden h-4"
        style={{ WebkitOverflowScrolling: 'touch', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ width: `${topScrollbarWidth}px`, height: '12px' }} />
      </div>

      <div
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        className="overflow-x-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table ref={tableRef} className="w-full" style={{ minWidth: '1300px' }}>
          <thead>
            <tr style={{ background: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)' }}>
              <th className="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedIds?.length === sorted?.length && sorted?.length > 0}
                  onChange={toggleAll}
                  className="rounded"
                  style={{ accentColor: 'var(--color-primary)' }}
                />
              </th>
              {[
                { label: 'Name', field: 'name' },
                { label: 'Timestamp', field: 'timestamp' },
                { label: 'Email', field: 'email' },
                { label: 'Number', field: 'phone' },
                { label: 'Profession', field: 'profession' },
                { label: 'Experience', field: 'experience' },
                { label: 'Company', field: 'company' },
                { label: 'Source', field: 'source' },
                { label: 'Assigned Rep', field: 'rep' },
                { label: 'Est. Value', field: 'value' },
                { label: 'Status', field: 'status' },
              ]?.map(({ label, field }) => (
                <th
                  key={field}
                  className="px-4 py-3 text-left cursor-pointer select-none"
                  onClick={() => toggleSort(field)}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                      {label}
                    </span>
                    <SortIcon field={field} />
                  </div>
                </th>
              ))}
              <th
                className="px-4 py-3 text-left sticky right-0"
                style={{ background: 'var(--color-muted)', borderLeft: '1px solid var(--color-border)', zIndex: 2 }}
              >
                <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paged?.map((lead, idx) => {
              const statusCfg = STATUS_CONFIG?.[lead?.status] || STATUS_CONFIG?.New;
              const isSelected = selectedIds?.includes(lead?.id);
              return (
                <tr
                  key={lead?.id}
                  style={{
                    background: isSelected ? 'rgba(var(--color-primary-rgb, 59,130,246), 0.05)' : idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2, var(--color-muted))',
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-muted)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2, var(--color-muted))'; }}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(lead?.id)}
                      className="rounded"
                      style={{ accentColor: 'var(--color-primary)' }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.timestamp}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.email}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.phone}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.profession}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.experience}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.company}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.source}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{lead?.rep}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>{formatRupees(lead?.value)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === lead?.id ? (
                      <select
                        autoFocus
                        value={mapLeadStatus(lead?.status)}
                        onChange={(e) => handleStatusChange(lead?.id, e?.target?.value)}
                        onBlur={() => setEditingId(null)}
                        className="text-xs px-2 py-1 rounded-full font-caption font-medium border-0 outline-none cursor-pointer"
                        style={{ background: statusCfg?.bg, color: statusCfg?.color }}
                      >
                        {Object.keys(STATUS_CONFIG)?.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold cursor-pointer"
                        style={{ background: statusCfg?.bg, color: statusCfg?.color }}
                        onClick={() => setEditingId(lead?.id)}
                        title={`Last activity: ${lead?.lastActivity}`}
                      >
                        {lead?.status}
                      </span>
                    )}
                  </td>
                  <td
                    className="px-4 py-3 sticky right-0"
                    style={{ background: 'var(--color-card)', borderLeft: '1px solid var(--color-border)', zIndex: 1 }}
                  >
                    <div className="flex items-center gap-1.5">
                      <button
                        className="flex items-center justify-center rounded-md transition-colors hover:bg-muted"
                        style={{ width: 28, height: 28, color: 'var(--color-text-secondary)' }}
                        title="Edit"
                        onClick={() => onEditLead && onEditLead(lead)}
                      >
                        <Icon name="Edit2" size={13} color="currentColor" />
                      </button>
                      {canDelete && (
                        <button
                          className="flex items-center justify-center rounded-md transition-colors hover:bg-red-50"
                          style={{ width: 28, height: 28, color: '#DC2626' }}
                          title="Delete"
                          onClick={() => {
                            setDeleteTargetId(lead?.id);
                            setIsDeleteOpen(true);
                          }}
                        >
                          <Icon name="Trash2" size={13} color="currentColor" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted?.length === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="SearchX" size={32} color="var(--color-text-secondary)" />
                    <span className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {leads?.length === 0 ? 'No leads yet' : 'No leads match your filters'}
                    </span>
                    {leads?.length === 0 && onAddNew && (
                      <button
                        onClick={onAddNew}
                        className="mt-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-colors"
                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                      >
                        Add your first lead
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--color-border)' }}>
        <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          Showing {firstShown}–{lastShown} of {sorted?.length} leads
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-md text-xs font-caption hover:bg-muted transition-colors disabled:opacity-40"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Previous
          </button>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((num) => (
            <button
              key={num}
              onClick={() => setPage(num)}
              className="px-3 py-1.5 rounded-md text-xs font-caption transition-colors"
              style={{
                background: page === num ? 'var(--color-primary)' : 'transparent',
                color: page === num ? '#fff' : 'var(--color-text-secondary)',
              }}
            >
              {num}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page >= pageCount}
            className="px-3 py-1.5 rounded-md text-xs font-caption hover:bg-muted transition-colors disabled:opacity-40"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Next
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        itemName="Lead"
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={async () => {
          if (deleteTargetId && onDeleteLead) {
            await onDeleteLead(deleteTargetId);
          }
          setLeads((prev) => prev.filter((lead) => lead.id !== deleteTargetId));
          setIsDeleteOpen(false);
          setDeleteTargetId(null);
        }}
      />
    </div>
  );
}
